import React, {FC, ReactNode, RefObject, useLayoutEffect} from 'react';
import styled from 'styled-components';

import chatBotAvatarSvg from '../../../../../libs/shared/assets/src/images/chatbotAvatar.svg';
import {Icon} from '../../../../../libs/shared/ui/src/components/icon/icon';
import {Layer} from '../../../../../libs/shared/ui/src/components/layers/layer';
import {shadows} from '../../../../../libs/shared/ui/src/styles/colorStyles';
import {fontSizes, fontWeights} from '../../../../../libs/shared/ui/src/styles/fontStyles';
import {LightUserThemeProvider} from '../../../../../libs/shared/ui/src/theme/userThemeProvider';
import {SandboxScenariosEnum, StepSlug} from '../../../../helpers/sandbox/sandboxScenarioHelpers';

/*
 * Props.
 */

interface SandboxChatPreviewProps {
  windowRef: RefObject<HTMLDivElement> | undefined;
  messageListRef: RefObject<HTMLDivElement>;
  articleRef: RefObject<HTMLDivElement> | undefined;
  billingMessageReplyRef: RefObject<HTMLDivElement> | undefined;
  handoffMessageRef: RefObject<HTMLDivElement> | undefined;
  step: string | undefined;
}

/*
 * Style.
 */

const StyledWindowDiv = styled.div`
  position: absolute;
  right: 20px;
  bottom: 20px;
  width: 384px;
  height: 640px;
  max-height: 80%;

  border-radius: 8px;
  background-color: ${(p) => p.theme.foreground};
  color: ${(p) => p.theme.text};
  ${shadows.dialogsAndDropdowns};

  display: flex;
  flex-direction: column;
`;

const StyledTitleDiv = styled.div`
  flex: none;
  padding: 6px;

  font-size: 13px;
  font-weight: ${fontWeights.bold};
  line-height: 16px;
  text-align: center;
`;

const StyledHeaderDiv = styled.div`
  flex: none;
  padding: 18px 16px;
  background-color: ${(p) => p.theme.brand};
  color: ${(p) => p.theme.greys.white};

  h1 {
    font-size: 16px;
    font-weight: ${fontWeights.bold};
    line-height: 19px;
    margin-top: 0;
    margin-bottom: 4px;
  }

  p {
    font-size: 12px;
    line-height: 14px;
    margin: 0;
  }
`;

const StyledMessageListDiv = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 0 16px;
`;

const agentAvatarWidth = 30;
const agentAvatarHorizontalGap = 4;

const StyledAgentNameDiv = styled.div`
  font-size: 12px;
  line-height: 14px;
  color: ${(p) => p.theme.greys.shade60};

  margin-top: 16px;
  margin-bottom: 8px;
  margin-left: ${agentAvatarWidth + agentAvatarHorizontalGap + 12}px;
`;

const StyledAgentFirstMessageDiv = styled.div`
  margin-top: 4px;
  margin-bottom: 4px;
  margin-right: 78px;

  display: flex;
  gap: 4px;
  align-items: flex-start;
`;

const StyledAgentAvatarImg = styled.img`
  flex: none;
  width: 30px;
  height: 30px;
`;

const StyledAgentFirstBubbleDiv = styled.div`
  background-color: ${(p) => p.theme.greys.shade20};
  border-radius: 18px 18px 18px 4px;
  padding: 8px 12px;

  font-size: 14px;
  line-height: 20px;
`;

const StyledAgentLastMessageDiv = styled.div`
  margin-top: 4px;
  margin-bottom: 4px;
  margin-left: ${agentAvatarWidth + agentAvatarHorizontalGap}px;
  margin-right: 78px;

  display: flex;
`;

const StyledAgentLastBubbleDiv = styled.div`
  background-color: ${(p) => p.theme.greys.shade20};
  border-radius: 4px 18px 18px 18px;
  padding: 8px 12px;

  font-size: 14px;
  line-height: 20px;
`;

const StyledAgentFooterDiv = styled.div`
  font-size: 12px;
  line-height: 14px;
  color: ${(p) => p.theme.greys.shade60};

  margin-top: 8px;
  margin-bottom: 16px;
  margin-left: ${agentAvatarWidth + agentAvatarHorizontalGap + 12}px;
`;

const StyledVisitorFirstMessageDiv = styled.div`
  margin-top: 4px;
  margin-bottom: 4px;
  margin-left: 112px;

  display: flex;
  justify-content: flex-end;
`;

const StyledVisitorFirstBubbleDiv = styled.div`
  background-color: ${(p) => p.theme.brand};
  border-radius: 18px 18px 4px 18px;
  padding: 8px 12px;

  color: ${(p) => p.theme.greys.white};
  font-size: 14px;
  line-height: 20px;
`;

const StyledVisitorFooterDiv = styled.div`
  font-size: 12px;
  line-height: 14px;
  color: ${(p) => p.theme.greys.shade60};
  text-align: right;

  margin-top: 8px;
  margin-bottom: 16px;
`;

const StyledSpacerDiv = styled.div`
  display: block;
  content: '';
  margin-top: 9px;
`;

const StyledReadMoreDiv = styled.div`
  font-weight: ${fontWeights.normal};
  font-size: ${fontSizes.verySmall};
  color: ${(p) => p.theme.greys.shade60};
  margin: 7px 0;
`;

const StyledEmbeddedKnowledgeBaseArticleListDiv = styled.div`
  margin-top: 9px;
  margin-bottom: 4px;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  outline: 2px solid ${(p) => p.theme.greys.shade30};
  background-color: ${(p) => p.theme.greys.white};
  border-radius: 8px;
`;

const StyledEmbeddedKnowledgeBaseArticleDiv = styled.div`
  padding: 7px 8px;
  display: flex;
  gap: 8px;
`;

const StyledEmbeddedKnowledgeBaseArticleIconDiv = styled.div`
  padding: 0;
  border-radius: 8px;
`;

const StyledEmbeddedKnowledgeBaseArticleContentDiv = styled.div`
  white-space: nowrap;

  flex-grow: 1;
  h1 {
    font-size: 14px;
    font-weight: ${fontWeights.bold};
    line-height: 20px;
    margin: 0;
    margin-bottom: 2px;
  }
`;

/*
 * Component.
 */

export const SandboxChatPreview: FC<SandboxChatPreviewProps> = (props) => (
  <Layer>
    <LightUserThemeProvider>
      {/* eslint-disable-next-line react/jsx-props-no-spreading */}
      <ChatWindow {...props} />
    </LightUserThemeProvider>
  </Layer>
);

const ChatWindow: FC<SandboxChatPreviewProps> = (props) => {
  const {messageListRef} = props;

  useLayoutEffect(() => {
    const messageListElement = messageListRef.current;
    if (!messageListElement) {
      return;
    }

    switch (props.step) {
      // V4: For the second tip, scroll to the billing message.
      case 'showcase-ai-answers' satisfies StepSlug<SandboxScenariosEnum.SCENARIO4_ACT4>: {
        const messageElement = props.billingMessageReplyRef?.current;
        if (!messageElement) {
          break;
        }

        scrollMessageList(messageListElement, messageElement, 102);
        break;
      }

      // V4: For the third tip, scroll to the billing message.
      case 'showcase-automation' satisfies StepSlug<SandboxScenariosEnum.SCENARIO4_ACT4>: {
        const messageElement = props.handoffMessageRef?.current;
        if (!messageElement) {
          break;
        }

        scrollMessageList(messageListElement, messageElement, 16);
        break;
      }

      default:
    }
  }, [props.step]);

  return renderChatTranscriptV4(props, messageListRef);
};

const AgentFirstMessage: FC<{children: ReactNode}> = (props) => (
  <>
    <StyledAgentNameDiv>Your fictional chatbot</StyledAgentNameDiv>
    <StyledAgentFirstMessageDiv>
      <StyledAgentAvatarImg src={chatBotAvatarSvg} />
      <StyledAgentFirstBubbleDiv>{props.children}</StyledAgentFirstBubbleDiv>
    </StyledAgentFirstMessageDiv>
  </>
);

const AgentMessageWrapper: FC<{children: ReactNode; messageRef?: RefObject<HTMLDivElement>}> = (props) => (
  <div ref={props.messageRef}>{props.children}</div>
);

const AgentLastMessage: FC<{children: ReactNode}> = (props) => (
  <StyledAgentLastMessageDiv>
    <StyledAgentLastBubbleDiv>{props.children}</StyledAgentLastBubbleDiv>
  </StyledAgentLastMessageDiv>
);

const VisitorFirstMessage: FC<{messageRef?: RefObject<HTMLDivElement>; children: ReactNode}> = (props) => (
  <StyledVisitorFirstMessageDiv ref={props.messageRef}>
    <StyledVisitorFirstBubbleDiv>{props.children}</StyledVisitorFirstBubbleDiv>
  </StyledVisitorFirstMessageDiv>
);

const EmbeddedKnowledgeBaseArticleList: FC<{articleRef?: RefObject<HTMLDivElement>}> = (props) => (
  <StyledEmbeddedKnowledgeBaseArticleListDiv>
    <StyledEmbeddedKnowledgeBaseArticleDiv ref={props.articleRef}>
      <StyledEmbeddedKnowledgeBaseArticleIconDiv>
        <Icon name="knowledgeBase" />
      </StyledEmbeddedKnowledgeBaseArticleIconDiv>
      <StyledEmbeddedKnowledgeBaseArticleContentDiv>
        <h1>Update account details</h1>
      </StyledEmbeddedKnowledgeBaseArticleContentDiv>
    </StyledEmbeddedKnowledgeBaseArticleDiv>
  </StyledEmbeddedKnowledgeBaseArticleListDiv>
);

function renderChatTranscriptV4(props: SandboxChatPreviewProps, messageListRef: RefObject<HTMLDivElement>) {
  return (
    <StyledWindowDiv ref={props.windowRef}>
      <StyledTitleDiv>
        <div>Chat preview</div>
      </StyledTitleDiv>

      <StyledHeaderDiv>
        <h1>Welcome</h1>
        <p>How can we help?</p>
      </StyledHeaderDiv>

      <StyledMessageListDiv ref={messageListRef}>
        <AgentFirstMessage>Hi there, how can I help you?</AgentFirstMessage>
        <StyledAgentFooterDiv>2m</StyledAgentFooterDiv>

        <VisitorFirstMessage>How can I update my billing address?</VisitorFirstMessage>
        <StyledVisitorFooterDiv>2m</StyledVisitorFooterDiv>

        <AgentMessageWrapper messageRef={props.billingMessageReplyRef}>
          <AgentFirstMessage>
            To update your billing address, follow these steps:
            <StyledSpacerDiv />
            <StyledSpacerDiv />
            1) Click the gear icon on the top right to go to settings.
            <StyledSpacerDiv />
            <StyledSpacerDiv />
            2) Click the “Billing” tab on the left navigation.
            <StyledSpacerDiv />
            <StyledSpacerDiv />
            3) Click the “Payment details” tab. Under “Billing address” section, click “Edit”, enter your new
            address, and save the changes.
            <StyledReadMoreDiv>Read more</StyledReadMoreDiv>
            <EmbeddedKnowledgeBaseArticleList />
          </AgentFirstMessage>
          <AgentLastMessage>Did that answer your question?</AgentLastMessage>
          <StyledAgentFooterDiv>1m</StyledAgentFooterDiv>
        </AgentMessageWrapper>

        <VisitorFirstMessage>Yes</VisitorFirstMessage>
        <StyledVisitorFooterDiv>1m</StyledVisitorFooterDiv>

        <AgentFirstMessage>Is there anything else I can help you with?</AgentFirstMessage>
        <StyledAgentFooterDiv>1m</StyledAgentFooterDiv>

        <VisitorFirstMessage>
          The tracking info for my package indicates it’s been delayed without further details. Could you
          investigate the reason behind the delay and provide an updated delivery estimate?
        </VisitorFirstMessage>
        <StyledVisitorFooterDiv>1m</StyledVisitorFooterDiv>
        <AgentMessageWrapper messageRef={props.handoffMessageRef}>
          <AgentFirstMessage>Let me connect you to an agent to help with that.</AgentFirstMessage>
          <StyledAgentFooterDiv>1m</StyledAgentFooterDiv>
        </AgentMessageWrapper>
      </StyledMessageListDiv>
    </StyledWindowDiv>
  );
}

function scrollMessageList(
  messageListElement: HTMLDivElement,
  targetElement: HTMLDivElement,
  visualOffset: number,
) {
  const messageListBoundingTop = messageListElement.getBoundingClientRect().top;
  const messageListContentBoundingTop = messageListBoundingTop - messageListElement.scrollTop;

  messageListElement.scrollTo({
    top: targetElement.getBoundingClientRect().top - messageListContentBoundingTop - visualOffset,
    behavior: 'smooth',
  });
}
